import { FC, useState, useEffect } from "react";
import Card from "components/common/Card";
import { TickIcon } from "components/common/Icons";
import { useAppSelector } from "utils/hooks";
import hospitalW from "assets/images/hospitalW.png";
import hospitalM from "assets/images/hospitalM.png";
import excM from "assets/images/excM.png";
import excW from "assets/images/excW.png";
import tooltipIcon from "assets/icons/tooltip.svg";
import { Tooltip } from "react-tooltip";

interface BenefitCardProps {
  id: string;
  cardData: any;
}

const screenData: any = {
  coverages: { name: "Coverages", borderColor: "border-[#CFE0EC]", bgColor: "bg-[#E5F8FF]", textColor: "text-[#0B7A9F]", tickColor: "#0B7A9F", bg: "#E5F8FF" },
  addOns: { name: "Add-on Coverages", borderColor: "border-[#D5E7C8]", bgColor: "bg-[#F3FFEA]", textColor: "text-[#78995D]", tickColor: "#78995D", bg: "#F3FFEA" }
};

const BenefitCard: FC<BenefitCardProps> = ({ id, cardData }) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  const policyData = useAppSelector((state) => state.rootReducer.kypSlice.policyData);
  const isPaymentDone = ["PAID", "WAIVEOFF"].includes(policyData?.kypPaymentStatus);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getWebImage = () => {
    switch (id) {
      case "coverages":
        return hospitalW;
      default:
        return excW;
    }
  };
  const getMobileImage = () => {
    switch (id) {
      case "coverages":
        return hospitalM;
      default:
        return excM;
    }
  };

  if (!isPaymentDone) {
    return (
      <div className="w-full relative">
        <img src={isMobile ? getMobileImage() : getWebImage()} className="lg:my-10 my-5 w-full object-cover" />
      </div>
    );
  }

  return (
    <>
      {/* Web View - Only visible on desktop */}
      <Card parentClassName="my-10 relative w-full hidden md:flex" className="w-full">
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-6 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        <div className={`absolute -top-5 -bottom-5 -left-3  w-[70%] border-[5px] rounded-[15px] ${screenData[id].borderColor} ${screenData[id].bgColor}`}></div>

        {Object.values(cardData || {})
          .filter((obj: any) => obj?.key)
          .map((obj: any, i: number, arr: any[]) => {
            return (
              <div key={i} className={`relative w-full -ml-2 ${obj?.key ? "flex" : "hidden"} ${i !== arr.length - 1 ? "border-b border-black/10" : ""}`}>
                {/* Left section – 55% */}
                <div className="flex items-start gap-3 p-5 w-[55%]">
                  <span className="mt-1">
                    <TickIcon color={screenData[id].tickColor} />
                  </span>

                  <div className="flex flex-col gap-1">
                    <p className={`text-sm md:text-base font-extrabold text-gray-900 ${obj?.isHighlighted ? (obj?.value === "No" ? " text-red-500" : "text-green-500") : ""}`}>{obj?.key}</p>

                    {obj?.tooltip && <p className={`text-xs text-gray-700 leading-snug ${obj?.isHighlighted && ""}`}>{obj.tooltip}</p>}
                  </div>
                </div>

                {/* Right section – 45% */}
                <div className="flex items-center justify-center px-5 py-3 w-[45%]">
                  <p className={`text-sm font-bold  text-center break-words ${obj?.isHighlighted ? (obj?.value === "No" ? "text-white bg-red-500 px-4 py-1 rounded-lg" : "rounded-lg bg-green-500 text-white px-4 py-1") : "text-gray-800"}`}>{obj?.value}</p>
                </div>
              </div>
            );
          })}
      </Card>
      {/* Mobile View */}
      <Card className={`w-full ${screenData[id].borderColor} border-2 rounded-[10px]`} parentClassName={`relative md:hidden mb-10  mt-6 rounded-[15px] flex`}>
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-5 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        {/* Render content for benefits cards on mobile */}

        {Object.values(cardData || {})
          .filter((obj: any) => obj?.key)
          .map((obj: any, i: number) => {
            return obj?.key ? (
              <div key={i} className={`block w-full`}>
                <div className={`flex gap-3 font-extrabold items-center w-full px-5 py-3 ${screenData[id].bgColor}`}>
                  <p className="w-4">
                    <TickIcon color={screenData[id].tickColor} />
                  </p>
                  <p>{obj?.key}</p>
                  {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}
                  <Tooltip delayShow={1} key={i} id={obj?.tooltip} place="bottom" className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg" render={() => <div className="text-white text-sm font-medium ">{obj?.tooltip}</div>} />
                </div>
                <p className="font-semibold text-gray-800 flex flex-wrap gap-1 text-center pl-10">{obj?.value}</p>
              </div>
            ) : null;
          })}
      </Card>
    </>
  );
};

export default BenefitCard;
